import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskMetricsTable = ({ metrics, onUpdateLimit }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'asset', direction: 'asc' });
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [editingLimit, setEditingLimit] = useState(null);
  const [newLimit, setNewLimit] = useState('');

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedMetrics = [...metrics]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'good':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getMetricColor = (value, threshold, type = 'lower') => {
    if (type === 'lower') {
      if (value > threshold * 1.2) return 'text-error';
      if (value > threshold) return 'text-warning';
    } else {
      if (value < threshold * 0.8) return 'text-error';
      if (value < threshold) return 'text-warning';
    }
    return 'text-success';
  };

  const handleLimitEdit = (assetId, currentLimit) => {
    setEditingLimit(assetId);
    setNewLimit(currentLimit?.toString());
  };

  const handleLimitSave = (assetId) => {
    if (onUpdateLimit) {
      onUpdateLimit(assetId, parseFloat(newLimit));
    }
    setEditingLimit(null);
    setNewLimit('');
  };

  const toggleMetricSelection = (assetId) => {
    setSelectedMetrics(prev => 
      prev?.includes(assetId) 
        ? prev?.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const selectAllMetrics = () => {
    if (selectedMetrics?.length === metrics?.length) {
      setSelectedMetrics([]);
    } else {
      setSelectedMetrics(metrics?.map(m => m?.asset));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Risk Metrics Overview</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {selectedMetrics?.length} of {metrics?.length} selected
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Download" 
            iconSize={14}
            disabled={selectedMetrics?.length === 0}
          >
            Export Selected
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3">
                <input
                  type="checkbox"
                  checked={selectedMetrics?.length === metrics?.length}
                  onChange={selectAllMetrics}
                  className="rounded border-border"
                />
              </th>
              {[
                { key: 'asset', label: 'Asset' },
                { key: 'allocation', label: 'Allocation %' },
                { key: 'beta', label: 'Beta' },
                { key: 'volatility', label: 'Volatility %' },
                { key: 'var', label: 'VaR (₹)' },
                { key: 'sharpe', label: 'Sharpe Ratio' },
                { key: 'maxDrawdown', label: 'Max DD %' },
                { key: 'correlation', label: 'Correlation' },
                { key: 'liquidity', label: 'Liquidity' },
                { key: 'concentration', label: 'Concentration' },
                { key: 'riskLimit', label: 'Risk Limit' },
                { key: 'status', label: 'Status' }
              ]?.map((column) => (
                <th key={column?.key} className="text-left p-3">
                  <button
                    onClick={() => handleSort(column?.key)}
                    className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <span>{column?.label}</span>
                    <Icon 
                      name={
                        sortConfig?.key === column?.key 
                          ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown')
                          : 'ChevronsUpDown'
                      } 
                      size={14} 
                    />
                  </button>
                </th>
              ))}
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMetrics?.map((metric) => (
              <tr key={metric?.asset} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedMetrics?.includes(metric?.asset)}
                    onChange={() => toggleMetricSelection(metric?.asset)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">
                        {metric?.asset?.substring(0, 2)}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{metric?.asset}</div>
                      <div className="text-xs text-muted-foreground">{metric?.type}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">{metric?.allocation?.toFixed(2)}%</span>
                    <div className="w-16 bg-border rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${Math.min(metric?.allocation, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`font-medium ${getMetricColor(metric?.beta, 1.2)}`}>
                    {metric?.beta?.toFixed(2)}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`font-medium ${getMetricColor(metric?.volatility, 25)}`}>
                    {metric?.volatility?.toFixed(2)}%
                  </span>
                </td>
                <td className="p-3">
                  <span className="font-medium text-error">
                    ₹{Math.abs(metric?.var)?.toLocaleString('en-IN')}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`font-medium ${getMetricColor(metric?.sharpe, 1, 'higher')}`}>
                    {metric?.sharpe?.toFixed(2)}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`font-medium ${getMetricColor(metric?.maxDrawdown, 15)}`}>
                    {metric?.maxDrawdown?.toFixed(2)}%
                  </span>
                </td>
                <td className="p-3">
                  <span className={`font-medium ${getMetricColor(Math.abs(metric?.correlation), 0.7)}`}>
                    {metric?.correlation?.toFixed(2)}
                  </span>
                </td>
                <td className="p-3">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                    metric?.liquidity === 'High' ? 'bg-success/10 text-success' :
                    metric?.liquidity === 'Medium'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                  }`}>
                    {metric?.liquidity}
                  </div>
                </td>
                <td className="p-3">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                    metric?.concentration === 'Low' ? 'bg-success/10 text-success' :
                    metric?.concentration === 'Medium'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                  }`}>
                    {metric?.concentration}
                  </div>
                </td>
                <td className="p-3">
                  {editingLimit === metric?.asset ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={newLimit}
                        onChange={(e) => setNewLimit(e?.target?.value)}
                        className="w-16 px-2 py-1 text-xs border border-border rounded"
                        step="0.1"
                      />
                      <button
                        onClick={() => handleLimitSave(metric?.asset)}
                        className="text-success hover:text-success/80"
                      >
                        <Icon name="Check" size={14} />
                      </button>
                      <button
                        onClick={() => setEditingLimit(null)}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleLimitEdit(metric?.asset, metric?.riskLimit)}
                      className="text-sm font-medium text-foreground hover:text-primary transition-smooth"
                    >
                      {metric?.riskLimit}%
                    </button>
                  )}
                </td>
                <td className="p-3">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${getStatusColor(metric?.status)}`}>
                    <Icon 
                      name={
                        metric?.status === 'critical' ? 'AlertTriangle' :
                        metric?.status === 'warning' ? 'AlertCircle' : 'CheckCircle'
                      } 
                      size={12} 
                      className="mr-1"
                    />
                    {metric?.status}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-1">
                    <button
                      className="p-1 text-muted-foreground hover:text-foreground transition-smooth"
                      title="View Details"
                    >
                      <Icon name="Eye" size={14} />
                    </button>
                    <button
                      className="p-1 text-muted-foreground hover:text-foreground transition-smooth"
                      title="Edit Settings"
                    >
                      <Icon name="Settings" size={14} />
                    </button>
                    <button
                      className="p-1 text-muted-foreground hover:text-error transition-smooth"
                      title="Remove from Portfolio"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {metrics?.length} assets • Last updated: {new Date()?.toLocaleString('en-IN')}
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="RefreshCw" iconSize={14}>
              Refresh
            </Button>
            <Button variant="outline" size="sm" iconName="Filter" iconSize={14}>
              Filter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskMetricsTable;